// Attendance > Grid (Mercedes, Nov 3, 2026): people down the side, days across the top,
// one code per cell, her spreadsheet's codes. Day, Week and Month views. A manager clicks
// a cell to change it for their own team; the clock's value stays underneath, the cell
// is marked edited, and who, when and why go in the change log (the server checks the team).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { attendance } from '../../services/api';
import {
  Code, Msg, box, btn, btn2, input, lbl, th, td, left, muted,
  addDays, monday, fmtShort, fmtDow, fmtMd, fmtDay, fmtWhen, clock, pts, todayEt,
} from './shared';

function rangeFor(view, anchor) {
  if (view === 'day') return { from: anchor, to: anchor };
  if (view === 'week') { const m = monday(anchor); return { from: m, to: addDays(m, 6) }; }
  const [y, mm] = anchor.split('-').map(Number);
  const from = `${y}-${String(mm).padStart(2, '0')}-01`;
  const to = new Date(Date.UTC(y, mm, 0)).toISOString().slice(0, 10);
  return { from, to };
}
function step(view, anchor, dir) {
  if (view === 'day') return addDays(anchor, dir);
  if (view === 'week') return addDays(anchor, 7 * dir);
  const [y, m] = anchor.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + dir, 1));
  return d.toISOString().slice(0, 10);
}

export default function TeamGrid({ me }) {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(todayEt());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [cell, setCell] = useState(null);
  const { from, to } = rangeFor(view, anchor);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await attendance('grid', { from, to })); } catch (e) { setErr(e.message || 'Could not load the grid.'); }
    setLoading(false);
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const out = [];
    for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
    return out;
  }, [from, to]);

  const canEdit = (p) => me.access.manage.includes(p.team);
  const showPoints = data && data.people.some((p) => p.points);
  const teams = data ? [...new Set(data.people.map((p) => p.team))] : [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {['day', 'week', 'month'].map((v) => (
          <button key={v} type="button" style={v === view ? btn : btn2} onClick={() => setView(v)}>{v[0].toUpperCase() + v.slice(1)}</button>
        ))}
        <span style={{ width: 10 }} />
        <button type="button" style={btn2} onClick={() => setAnchor(step(view, anchor, -1))}>Back</button>
        <button type="button" style={btn2} onClick={() => setAnchor(todayEt())}>Today</button>
        <button type="button" style={btn2} onClick={() => setAnchor(step(view, anchor, 1))}>Next</button>
        <input type="date" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} style={input} />
        <b style={{ marginLeft: 6 }}>{from === to ? fmtShort(from) : `${fmtDay(from)} to ${fmtDay(to)}`}</b>
        {loading && <span style={muted}>Reading the time clock…</span>}
      </div>
      <Msg err={err} />
      <Legend codes={me.codes} />

      {data && view === 'day' && <DayList data={data} day={from} onCell={(p, d) => setCell({ p, d })} />}
      {data && view !== 'day' && teams.map((t) => {
        const people = data.people.filter((p) => p.team === t);
        return (
          <div key={t} style={{ ...box, marginBottom: 14 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...left, position: 'sticky', left: 0, background: '#fff', minWidth: 170 }}>{me.teams[t]}</th>
                  {days.map((d) => (
                    <th key={d} style={{ ...th, textAlign: 'center', padding: '4px 2px', background: d === data.today ? '#eef4fa' : undefined, color: data.holidays.includes(d) ? '#b42318' : th.color }}>
                      <div>{fmtDow(d).slice(0, 2)}</div><div style={{ fontWeight: 400 }}>{fmtMd(d)}</div>
                    </th>
                  ))}
                  {showPoints && <th style={th} title="Approved points in the last 12 months">Points</th>}
                </tr>
              </thead>
              <tbody>
                {people.map((p) => {
                  const byDate = new Map(p.days.map((d) => [d.date, d]));
                  return (
                    <tr key={p.key} style={{ borderTop: '1px solid #eef0f2' }}>
                      <td style={{ ...td, ...left, position: 'sticky', left: 0, background: '#fff', fontWeight: 600 }}>
                        {p.name}{p.seasonal ? <span style={{ fontWeight: 400, color: '#6b7280' }}> (seasonal)</span> : null}
                        {p.balance && <div style={{ fontWeight: 400, fontSize: 11, color: '#6b7280' }} title={`From the RUN report for the ${fmtDay(p.balance.asOf)} payroll`}>PTO {hrsOf(p.balance.pto)}, sick {hrsOf(p.balance.sick)} as of {fmtMd(p.balance.asOf)}</div>}
                      </td>
                      {days.map((d) => {
                        const x = byDate.get(d);
                        return (
                          <td key={d} style={{ textAlign: 'center', padding: '3px 2px', background: x && x.weekend ? '#fafafa' : undefined, cursor: 'pointer', position: 'relative' }}
                            onClick={() => x && setCell({ p, d: x })}
                            title={x ? cellTitle(x, me.codes) : ''}>
                            {x && <Code code={x.code} small />}
                            {x && x.edited && <span style={{ position: 'absolute', top: 1, right: 2, width: 6, height: 6, borderRadius: 3, background: '#b42318' }} title="Edited" />}
                            {x && x.missedClock && x.missedClock.length > 0 && <span style={{ position: 'absolute', bottom: 0, right: 2, fontSize: 10, color: '#8a5a00', fontWeight: 700 }} title="Missed punch">!</span>}
                          </td>
                        );
                      })}
                      {showPoints && (
                        <td style={{ ...td, fontWeight: p.points && p.points.total >= 5 ? 700 : undefined, color: p.points && p.points.total >= 5 ? '#b42318' : undefined }}>
                          {p.points ? pts(p.points.total) : ''}{p.points && p.points.pending ? <span style={{ color: '#8a5a00' }} title="Pending review"> +{p.points.pending}?</span> : null}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
      {data && !data.people.length && <p>Nobody on your team yet. Mercedes sets the teams on the Team tab.</p>}
      <p style={muted}>A red dot means someone changed the day by hand; the time clock's value is kept underneath. "!" marks a missed punch. Click any day for the detail.</p>

      {cell && <CellEditor me={me} p={cell.p} d={cell.d} canEdit={canEdit(cell.p)} onClose={() => setCell(null)} onSaved={() => { setCell(null); load(); }} />}
    </div>
  );
}

const hrsOf = (n) => (n == null ? '-' : Number(n) < 0 ? `(${Math.abs(Number(n)).toFixed(2)})` : Number(n).toFixed(2));

function cellTitle(x, codes) {
  const parts = [codes[x.code] || ''];
  if (x.edited) parts.push(`Changed from ${codes[x.clock] || 'blank'} by ${x.override.by}`);
  if (x.schedule) parts.push(x.schedule.off ? 'Scheduled off' : `Scheduled ${clock(x.schedule.start)} to ${clock(x.schedule.end)}`);
  if (x.first) parts.push(`In ${clock(x.first)}${x.last ? `, out ${clock(x.last)}` : ''}`);
  return parts.filter(Boolean).join('. ');
}

function Legend({ codes }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10, fontSize: 12 }}>
      {Object.entries(codes).map(([k, v]) => <span key={k}><Code code={k} small /> {v}</span>)}
    </div>
  );
}

function DayList({ data, day, onCell }) {
  return (
    <div style={{ ...box, marginBottom: 14 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr>
          <th style={{ ...th, ...left }}>Person</th><th style={{ ...th, ...left }}>Code</th><th style={{ ...th, ...left }}>Scheduled</th>
          <th style={th}>In</th><th style={th}>Out</th><th style={th}>Worked</th><th style={{ ...th, ...left }}>Time off</th><th style={{ ...th, ...left }}>Flags</th>
        </tr></thead>
        <tbody>
          {data.people.map((p) => {
            const d = p.days.find((x) => x.date === day);
            if (!d) return null;
            return (
              <tr key={p.key} onClick={() => onCell(p, d)} style={{ borderTop: '1px solid #eef0f2', cursor: 'pointer' }}>
                <td style={{ ...td, ...left, fontWeight: 600 }}>{p.name}</td>
                <td style={{ ...td, ...left }}><Code code={d.code} /> {d.edited ? <span style={muted}>edited</span> : null}</td>
                <td style={{ ...td, ...left }}>{d.schedule ? (d.schedule.off ? 'Off' : `${clock(d.schedule.start)} to ${clock(d.schedule.end)}`) : ''}</td>
                <td style={td}>{clock(d.first)}</td>
                <td style={td}>{d.open ? 'still in' : clock(d.last)}</td>
                <td style={td}>{d.worked ? d.worked.toFixed(2) : ''}</td>
                <td style={{ ...td, ...left }}>{d.leave.map((l) => `${l.type} ${l.hours.toFixed(2)}`).join(', ')}</td>
                <td style={{ ...td, ...left, whiteSpace: 'normal', color: '#8a5a00' }}>
                  {d.lateBy != null ? `Late ${d.lateBy} min. ` : ''}{d.earlyBy != null ? `Left ${d.earlyBy} min early. ` : ''}
                  {(d.missedClock || []).map((m) => m.text).join('; ')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CellEditor({ me, p, d, canEdit, onClose, onSaved }) {
  const [code, setCode] = useState(d.override ? d.override.code : '');
  const [reason, setReason] = useState('');
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    attendance('override-log', { person_key: p.key, day: d.date }).then((r) => setLog(r.log), () => setLog([]));
  }, [p.key, d.date]);

  const save = async (clear) => {
    setBusy(true); setErr('');
    try {
      await attendance('override-save', { person_key: p.key, day: d.date, code: clear ? null : code, clock_code: d.clock, reason });
      onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ ...box, padding: 18, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ margin: '0 0 8px' }}>{p.name}, {fmtShort(d.date)}</h3>
          <button type="button" style={btn2} onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 13, display: 'grid', gridTemplateColumns: '130px 1fr', rowGap: 4, marginBottom: 12 }}>
          <span style={muted}>Showing</span><span><Code code={d.code} /> {me.codes[d.code] || 'Nothing'}{d.edited ? ' (changed by hand)' : ''}</span>
          <span style={muted}>Time clock says</span><span><Code code={d.clock} /> {me.codes[d.clock] || 'Nothing'}</span>
          <span style={muted}>Scheduled</span><span>{d.schedule ? (d.schedule.off ? 'Off' : `${clock(d.schedule.start)} to ${clock(d.schedule.end)}`) : 'No schedule'}</span>
          <span style={muted}>Punches</span><span>{d.work.length ? d.work.map((w) => `${clock(w.in)} to ${w.out ? clock(w.out) : '(no clock-out)'}`).join(', ') : 'None'}</span>
          {d.worked > 0 && <><span style={muted}>Worked</span><span>{d.worked.toFixed(2)} hours</span></>}
          {d.leave.length > 0 && <><span style={muted}>Time off</span><span>{d.leave.map((l) => `${l.type} ${l.hours.toFixed(2)}`).join(', ')}</span></>}
          {(d.lateBy != null || d.earlyBy != null) && <><span style={muted}>Late / early</span><span>{d.lateBy != null ? `In ${d.lateBy} min late` : ''}{d.lateBy != null && d.earlyBy != null ? ', ' : ''}{d.earlyBy != null ? `out ${d.earlyBy} min early` : ''}</span></>}
          {d.missedClock && d.missedClock.length > 0 && <><span style={muted}>Missed punch</span><span style={{ color: '#8a5a00' }}>{d.missedClock.map((m) => m.text).join('; ')}</span></>}
          {d.notes && d.notes.length > 0 && <><span style={muted}>Time clock notes</span><span>{d.notes.map((n) => `${clock(n.time)}: ${n.note}`).join(' / ')}</span></>}
        </div>

        {canEdit ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
            <label style={lbl}>Change to
              <select value={code} onChange={(e) => setCode(e.target.value)} style={input}>
                <option value="">Pick a code</option>
                {me.overrideCodes.map((c) => <option key={c} value={c}>{c}: {me.codes[c]}</option>)}
              </select>
            </label>
            <label style={{ ...lbl, flex: '1 1 220px' }}>Why (goes in the change log)
              <input value={reason} onChange={(e) => setReason(e.target.value)} style={input} placeholder="Called Rachael at 7:40, sick kid" />
            </label>
            <button type="button" style={btn} disabled={busy || !code || !reason.trim()} onClick={() => save(false)}>Save</button>
            {d.edited && <button type="button" style={btn2} disabled={busy || !reason.trim()} onClick={() => save(true)} title="Go back to what the time clock says">Back to the clock</button>}
          </div>
        ) : <p style={muted}>Only {p.team === 'cr' ? 'Rachael' : p.team === 'ops' ? 'Carman (or Matt Jr)' : 'Mercedes'} and Mercedes can change this day.</p>}
        <Msg err={err} />

        <div style={{ fontSize: 12 }}>
          <b>Change log</b>
          {log === null && <div style={muted}>Loading…</div>}
          {log && !log.length && <div style={muted}>No changes. This is what the time clock says.</div>}
          {log && log.map((l, i) => (
            <div key={i} style={{ borderTop: '1px solid #eef0f2', padding: '4px 0' }}>
              {fmtWhen(l.created_at)}, {l.created_by}: {l.code ? <>set to <Code code={l.code} small /></> : 'back to the clock'}
              {l.clock_code ? <span style={muted}> (clock said {l.clock_code})</span> : null}. {l.reason}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
